import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { brand, navLinks, contactInfo } from "../mock";

const Wordmark = ({ light }) => (
  <div className="flex flex-col leading-none select-none">
    <span
      className={`font-sans-ui tracking-brand text-[1.15rem] md:text-[1.35rem] font-500 ${
        light ? "text-cream" : "text-[var(--navy)]"
      }`}
      style={{ color: light ? "var(--cream)" : "var(--navy)" }}
    >
      SOUTH&nbsp;SEA
    </span>
    <span
      className="font-sans-ui tracking-wide-2 text-[0.55rem] md:text-[0.6rem] mt-1"
      style={{ color: light ? "rgba(246,243,238,0.75)" : "var(--teal)" }}
    >
      DESIGN STUDIO
    </span>
  </div>
);

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const isHome = location.pathname === "/";
  const solid = scrolled || !isHome;

  const handleNav = (link) => {
    setOpen(false);
    if (link.type === "route") {
      navigate(link.to);
      window.scrollTo({ top: 0 });
      return;
    }
    // section on home
    const goScroll = () => {
      if (link.target === "top") {
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        const el = document.getElementById(link.target);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    };
    if (!isHome) {
      navigate("/");
      setTimeout(goScroll, 420);
    } else {
      goScroll();
    }
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
          solid ? "bg-[var(--cream)]/95 backdrop-blur-md py-4 shadow-[0_1px_20px_rgba(22,50,79,0.06)]" : "bg-transparent py-6"
        }`}
      >
        <div className="max-w-[1500px] mx-auto px-6 md:px-10 flex items-center justify-between">
          <button onClick={() => handleNav({ type: "route", to: "/" })} className="cursor-pointer">
            <Wordmark light={!solid} />
          </button>

          <nav className="hidden lg:flex items-center gap-9">
            {navLinks.slice(1).map((l) => (
              <button
                key={l.label}
                onClick={() => handleNav(l)}
                className={`link-underline font-sans-ui tracking-wide-3 text-[0.72rem] uppercase transition-colors duration-300 ${
                  solid ? "text-[var(--navy)]" : "text-cream"
                }`}
                style={{ color: solid ? "var(--navy)" : "var(--cream)" }}
              >
                {l.label}
              </button>
            ))}
          </nav>

          <button
            onClick={() => setOpen(true)}
            className="flex items-center gap-3 group"
            aria-label="Open menu"
          >
            <span
              className={`hidden sm:block font-sans-ui tracking-wide-3 text-[0.68rem] uppercase ${
                solid ? "text-[var(--navy)]" : "text-cream"
              }`}
              style={{ color: solid ? "var(--navy)" : "var(--cream)" }}
            >
              Menu
            </span>
            <Menu
              size={22}
              strokeWidth={1.4}
              style={{ color: solid ? "var(--navy)" : "var(--cream)" }}
            />
          </button>
        </div>
      </header>

      {/* Full-screen overlay menu */}
      <div
        className={`fixed inset-0 z-[60] transition-all duration-700 ${
          open ? "visible" : "invisible"
        }`}
      >
        <div
          className={`absolute inset-0 bg-[var(--navy-900)] transition-opacity duration-700 ${
            open ? "opacity-100" : "opacity-0"
          }`}
          style={{ backgroundColor: "var(--navy-900)" }}
        />
        <div
          className={`relative h-full flex flex-col transition-all duration-700 ${
            open ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-4"
          }`}
        >
          <div className="max-w-[1500px] mx-auto w-full px-6 md:px-10 py-6 flex items-center justify-between">
            <Wordmark light />
            <button onClick={() => setOpen(false)} aria-label="Close menu" className="text-cream">
              <X size={26} strokeWidth={1.4} style={{ color: "var(--cream)" }} />
            </button>
          </div>

          <div className="flex-1 max-w-[1500px] mx-auto w-full px-6 md:px-10 grid md:grid-cols-[1.4fr_1fr] gap-12 items-center">
            <nav className="flex flex-col gap-1">
              {navLinks.map((l, i) => (
                <button
                  key={l.label}
                  onClick={() => handleNav(l)}
                  className="menu-link text-left font-serif-display text-[2.5rem] md:text-[3.6rem] leading-[1.15] text-cream"
                  style={{ color: "var(--cream)", transitionDelay: `${i * 40}ms` }}
                >
                  {l.label}
                </button>
              ))}
            </nav>

            <div className="hidden md:block border-l border-white/15 pl-12">
              <p className="font-sans-ui tracking-wide-2 text-[0.62rem] uppercase text-[var(--teal-light)] mb-5">
                Get in touch
              </p>
              <a
                href={`tel:${contactInfo.phone}`}
                className="flex items-center gap-3 text-cream/90 mb-3 link-underline"
                style={{ color: "rgba(246,243,238,0.9)" }}
              >
                <Phone size={15} strokeWidth={1.5} /> {contactInfo.phone}
              </a>
              <a
                href={`mailto:${contactInfo.email}`}
                className="block text-cream/90 mb-6 link-underline"
                style={{ color: "rgba(246,243,238,0.9)" }}
              >
                {contactInfo.email}
              </a>
              <p className="text-cream/60 text-sm" style={{ color: "rgba(246,243,238,0.6)" }}>
                {contactInfo.location}
              </p>
              <div className="flex gap-3 mt-10">
                {brand.pillars.map((p) => (
                  <span
                    key={p}
                    className="font-sans-ui tracking-wide-3 text-[0.6rem] uppercase text-[var(--sand)]"
                    style={{ color: "var(--sand)" }}
                  >
                    {p}
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className="max-w-[1500px] mx-auto w-full px-6 md:px-10 py-8 border-t border-white/10">
            <p className="font-script text-[var(--teal-light)] text-3xl" style={{ color: "var(--teal-light)" }}>
              Better spaces, brighter tomorrows
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
